/**
 * Copy resolvers for the push-notification settings panel.
 *
 * ⚠️ THIS MODULE NO LONGER HOLDS COPY. It used to, on an explicit assumption written into its own
 * header and into `be/services/notifications/types.js`: *"this surface is core-staff only, and
 * English is scoped to the cleaning/service crews, so no English-locale user reaches it."*
 *
 * The `guest_checkout` notification type broke that assumption on 2026-08-04
 * (tasks/manual-guest-checkout-event.md D31/D32). The cleaning crew can now register a device and
 * hold a preference, and the crew is exactly who English exists for in this product. So the strings
 * moved to `locales/{ar,en}.js` under `notifications.*`, and what remains here is the mapping layer:
 * backend keys and error codes → locale keys.
 *
 * WHY A MAPPING LAYER AT ALL, rather than `t('notifications.errors.' + code)` inline: the backend's
 * codes (`PUSH_001`…) are opaque, and a locale file full of `PUSH_001:` reads to a translator as
 * noise. Naming them once here keeps the locale files legible and keeps the "must match backend"
 * contract in one auditable place.
 *
 * ⚠️ The TYPE keys are the backend's registry keys (`be/services/notifications/types.js`) and the
 * CODE keys are the backend's error codes. Both are persisted / contractual: renaming one silently
 * produces an unlabelled toggle or an untranslated error, not a build failure.
 */

import { NOTIFICATION_ERROR_CODES } from '../utils/notificationErrorCodes';

/**
 * Backend error code → locale key under `notifications.errors`.
 *
 * `API_004` is not a PUSH_xxx code — it is the SHARED rate-limit code, reached from the 5/min
 * ceiling on «أرسل لي إشعارًا تجريبيًا». It belongs here because the install session is precisely
 * when someone taps that button repeatedly, and the generic fallback used to tell them to "try
 * again" — advice guaranteed to fail again.
 */
const ERROR_CODE_KEYS = {
  API_004: 'API_004',
  [NOTIFICATION_ERROR_CODES.DISABLED]: 'PUSH_DISABLED',
  [NOTIFICATION_ERROR_CODES.NOT_CONFIGURED]: 'PUSH_NOT_CONFIGURED',
  [NOTIFICATION_ERROR_CODES.INVALID_SUBSCRIPTION]: 'PUSH_INVALID_SUBSCRIPTION',
  [NOTIFICATION_ERROR_CODES.DEVICE_NOT_FOUND]: 'PUSH_DEVICE_NOT_FOUND',
  [NOTIFICATION_ERROR_CODES.UNKNOWN_TYPE]: 'PUSH_UNKNOWN_TYPE',
  [NOTIFICATION_ERROR_CODES.NO_DEVICES]: 'PUSH_NO_DEVICES',
  [NOTIFICATION_ERROR_CODES.SEND_FAILED]: 'PUSH_SEND_FAILED',
  [NOTIFICATION_ERROR_CODES.APP_SIGNIN_REQUIRED]: 'PUSH_APP_SIGNIN_REQUIRED'
};

/** The registry keys this build knows how to label. Anything else falls back to `unknown`. */
const KNOWN_TYPE_KEYS = ['guest_message', 'lock_incident', 'guest_checkout', 'lock_battery', 'clean_target', 'cleaning_arrival', 'booking_conflict', 'booking_desync', 'owner_new_booking', 'owner_booking_cancelled'];

/**
 * Label + description for one notification type.
 *
 * A type the backend knows and this build does not resolves to «إشعار» rather than to a blank row —
 * a frontend deployed behind the backend must still render an operable toggle.
 */
export const typeLabel = (t, key) => {
  const resolved = KNOWN_TYPE_KEYS.includes(key) ? key : 'unknown';
  return {
    label: t(`notifications.types.${resolved}.label`),
    description: t(`notifications.types.${resolved}.description`)
  };
};

/**
 * The browser's own permission state, reported verbatim.
 *
 * The known-state list is load-bearing: `t()` echoes the KEY back when a translation is missing, so
 * an unrecognised state would render `notifications.permission.whatever` on screen rather than
 * falling through to the raw value.
 */
const PERMISSION_STATES = ['granted', 'denied', 'default', 'unsupported'];

export const permissionLabel = (t, state) =>
  (PERMISSION_STATES.includes(state) ? t(`notifications.permission.${state}`) : state);

/**
 * Per-device confidence (a subscription is a monitored asset, not a static registration).
 *
 * The icon is not translated — it is a glyph, and translating a tick would be a category error.
 */
const HEALTH_ICONS = { ok: '✓', stale: '⚠', unknown: '•' };

export const deviceHealth = (t, health) => {
  const resolved = HEALTH_ICONS[health] ? health : 'unknown';
  return { label: t(`notifications.health.${resolved}`), icon: HEALTH_ICONS[resolved] };
};

/**
 * «تم الإرسال إلى N من أجهزتك» — with Arabic number agreement, which is not optional here.
 *
 * Arabic marks one, two, and many as three different grammatical forms. The server only reports
 * success when at least one device accepted, so «إلى ١ من أجهزتك» was the MOST COMMON rendering of
 * this message and it reads as broken Arabic to a native speaker. The dual («جهازيك») has no English
 * equivalent and is exactly the kind of thing a count-plus-noun template cannot express — which is
 * why the three forms are three separate keys rather than one interpolation.
 *
 * @param {Function} t
 * @param {number} accepted
 * @param {Function} formatCount  Locale-aware digits — passed in so this module stays presentation-free
 */
export const testSentMessage = (t, accepted, formatCount) => {
  if (accepted === 1) return t('notifications.testSent.one');
  if (accepted === 2) return t('notifications.testSent.two');
  return t('notifications.testSent.many').replace('{count}', formatCount(accepted));
};

/** Resolve a known backend code to the reader's language, or null when we do not recognise it. */
export const messageForCode = (t, code) => {
  const key = ERROR_CODE_KEYS[code];
  return key ? t(`notifications.errors.${key}`) : null;
};

/**
 * Resolve a failed request to the reader's language — the function the UI actually calls.
 *
 * THREE TIERS, in the order `notificationErrorCodes.js` specifies: a code we know → the server's own
 * message → a generic apology. The middle tier matters: the convention is *"if a response carries
 * `code`, translate it; otherwise fall back to displaying the server's message"*, and without it any
 * response this build does not recognise — a shared code, a newer backend, a guard predating the
 * code convention — would replace a specific, correct sentence with "something went wrong".
 *
 * ⚠️ The server's string is ARABIC by convention (I18N_STRATEGY: the API is language-neutral and its
 * `error` field stays Arabic for logs and legacy clients). So for an English-locale reader this
 * middle tier can surface Arabic. That is deliberate and is the lesser evil: a specific message in
 * the wrong language is more actionable than a correct-language non-answer, and the real fix is a
 * code for that case on the backend — not a worse fallback here.
 */
export const messageForError = (t, err) => {
  const data = err?.response?.data;
  return messageForCode(t, data?.code)
    || (typeof data?.error === 'string' && data.error ? data.error : null)
    || t('notifications.errors.generic');
};

/**
 * Why a browser-side subscribe attempt did not produce a subscription. These are the machine reasons
 * `utils/serviceWorker.js` returns — it renders nothing itself, by design.
 */
const SUBSCRIBE_FAILURE_REASONS = ['unsupported', 'not_configured', 'denied', 'dismissed', 'subscribe_failed'];

export const subscribeFailureMessage = (t, reason) =>
  (SUBSCRIBE_FAILURE_REASONS.includes(reason)
    ? t(`notifications.subscribeFailure.${reason}`)
    : t('notifications.errors.generic'));
